import { LibraryIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PickerSourcesApi } from '@nucleus-commerce/admin-kit/resource-picker';
import { Button } from '@nucleus-commerce/ui/components/button';

import { CollectionPicker } from './collection-picker';

export interface FromCollectionSourceProps {
  api: PickerSourcesApi;
  /** The store whose collections are offered. */
  storeId: string;
  /** The picker's "Select all matching" limit; null when it has none (a capped picker). */
  selectAll: number | null;
}

/**
 * "From collection": pick one of the store's collections, filter the list by it and, where the
 * picker can, select every product it holds. When it cannot (no select-all here, or more products
 * than the limit), the filter stays and a note says to tick the rows.
 */
export function FromCollectionSource({ api, storeId, selectAll }: FromCollectionSourceProps) {
  const { t, i18n } = useTranslation('resourcePickers');
  const number = new Intl.NumberFormat(i18n.language);
  const [picking, setPicking] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 px-2 text-xs font-normal"
        onClick={() => {
          setNote(null);
          setPicking(true);
        }}
      >
        <LibraryIcon aria-hidden />
        {t('fromCollection.action')}
      </Button>
      {note ? (
        <p role="status" className="text-muted-foreground text-xs">
          {note}
        </p>
      ) : null}
      {picking ? (
        <CollectionPicker
          open
          onOpenChange={setPicking}
          storeId={storeId}
          title={t('fromCollection.title')}
          confirmLabel={t('fromCollection.confirm')}
          onConfirm={(selection) => {
            const collection = selection.items[0];
            if (!collection) return;
            api.setFilters({
              ...api.filters,
              collectionId: { op: 'in', values: [collection.id] },
            });
            if (selectAll === null) {
              setNote(t('fromCollection.tick', { title: collection.title }));
              return;
            }
            // The product picker selects once the filtered list (and its total) has loaded.
            void api.selectAllMatching().then((done) => {
              if (done) return;
              setNote(
                t('fromCollection.tooMany', {
                  title: collection.title,
                  limit: number.format(selectAll),
                }),
              );
            });
          }}
        />
      ) : null}
    </>
  );
}
